'use client';
import React, { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { saveOrderDraftTemplate } from 'apollo/templates/order';
import { actionOrderConstructionsDraft } from 'components/order-flow/shared/order-input-helper';
import {
    emptyConstruction,
    extractNumberOfUnitTypes,
    extractPropertyTypes,
    aggregateTeethOption,
    aggregateMaterials,
} from 'components/order-flow/action-order/constructions-step/constructions/construction-form/construction-form-helper';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { CONSTANTS } from 'config/constants';
import {
    ActionConstruction,
    ActionConstructionPropertyValues,
    NumberOfUnitSettingsOption,
} from 'models/order-input/action-order';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { customerSettings } from 'utils/customerSettings';
import { Action } from 'models/action';
import { ConstructionPropertyType } from 'models/order-input/shared';
import { toCamelCase } from 'utils/trim-string';
import { GroupMaterials } from 'models/organization';
import { TeethRange } from 'models/material';

interface ConstructionContextType {
    construction: ActionConstruction;
    isLoading: boolean;
    isNextBtnDisabled: boolean;
    isCommentRequired: boolean;
    isThreeShapeEnabled: boolean;
    primaryAction: Action;
    subCategoryId: string;
    numberOfUnitsOption: NumberOfUnitSettingsOption[];
    properties: {
        type: ConstructionPropertyType;
        isRequired: boolean;
    }[];
    isConstructionValid: boolean;
    availableMaterials: string[];
    availableMaterialObjs: GroupMaterials[];
    aggregatedTeethOption: string | null;
    numberOfUnitsValues: {
        header: string;
        numberOfUnits: number;
        actionId: string;
    }[];
    setConstruction: React.Dispatch<React.SetStateAction<ActionConstruction>>;
    saveDraftConstructions: () => Promise<void>;
    handleSettingsChanged: (settings: ActionConstructionPropertyValues) => void;
    onSettingsSelection: (propertyName: string, settingValue: string | boolean) => void;
    onShadeChange: (properties: { shade: string; color: string; colorSampling: boolean }) => void;
    onMaterialsChange: (properties: {
        material: string;
        otherMaterial: string;
        materialId?: string | undefined;
    }) => void;
    onNumberOfUnitsChange: (value: number, header: string, actionId: string) => void;
    onTeethSettingsChange: (
        propertyName: string,
        settingValue: {
            id: number;
        }[],
    ) => void;
    hasType: (type: ConstructionPropertyType) => boolean;
    isRequired: (type: ConstructionPropertyType) => boolean;
    handleTeethSelectionChanged: (selectedTeeth?: number[] | undefined, teethRange?: TeethRange | undefined) => void;
    handleActionsChanged: (actions: Action[]) => void;
}

const ConstructionContext = createContext<ConstructionContextType | undefined>(undefined);

export function ConstructionProvider({ children }: { children: ReactNode }) {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [construction, setConstruction] = useState<ActionConstruction>(emptyConstruction());

    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { order } = useContext(ActionOrderContext);
    const currentOrg = currentOrgVar();
    const isCommentRequired = customerSettings.getCustomerSetting(
        currentOrg?.customerSettings,
        CONSTANTS.SETTINGS_NAMES.MANDATORY_COMMENTS,
    );
    const isThreeShapeEnabled = customerSettings.getCustomerSetting(
        currentOrg?.customerSettings,
        CONSTANTS.SETTINGS_NAMES.THREE_SHAPE_INTEGRATION,
    );

    const currentUser = currentUserVar();
    const userConstructionSettings = currentUser.constructionSetting;

    const [saveOrderDraft, { loading }] = useMutation(saveOrderDraftTemplate, {
        onCompleted: () => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_SAVED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { primaryAction, availableMaterials, availableMaterialObjs, aggregatedTeethOption } = useMemo(() => {
        const primaryAction =
            construction.actions && construction.actions.length > 0 ? construction.actions[0] : ({} as Action);

        // Aggregate materials from all actions, filtering for unique values by name
        const { availableMaterials, availableMaterialObjs } = aggregateMaterials(construction.actions);

        // Aggregate teeth option from all actions using priority:
        // "Single tooth" > "Range" > "Up/low jaw" > null
        const aggregatedTeethOption = aggregateTeethOption(construction.actions);

        return {
            primaryAction,
            availableMaterials,
            availableMaterialObjs: availableMaterialObjs as GroupMaterials[],
            aggregatedTeethOption,
        };
    }, [construction]);

    const subCategoryId = useMemo(() => {
        return construction.actions && construction.actions.length > 0
            ? construction.actions[0].subCategory[0]._id || ''
            : '';
    }, [construction]);

    const { properties, numberOfUnitsOption } = useMemo(() => {
        return {
            properties: extractPropertyTypes(construction.actions),
            numberOfUnitsOption: extractNumberOfUnitTypes(construction.actions),
        };
    }, [construction.actions]);

    const hasType = (type: ConstructionPropertyType) => {
        return properties.some((prop) => prop.type === type);
    };

    const defaultSettings = useMemo<ActionConstructionPropertyValues>(() => {
        const defaults = {} as ActionConstructionPropertyValues;

        if (hasType(ConstructionPropertyType.CONTACT_TYPE)) {
            defaults.contactType = userConstructionSettings?.contactType || 'NORMAL';
        }
        if (hasType(ConstructionPropertyType.METAL_DESIGN)) {
            defaults.design = userConstructionSettings?.metalDesign || 'NO_METAL_MARGIN';
        }
        if (hasType(ConstructionPropertyType.PONTIC_DESIGN)) {
            defaults.ponticDesign = userConstructionSettings?.ponticDesign || 'BULLET';
        }
        if (hasType(ConstructionPropertyType.OCCLUSION_TYPE)) {
            defaults.occlusion = userConstructionSettings?.occlusionContact || 'NORMAL';
        }

        return defaults;
    }, [properties, userConstructionSettings]);

    useEffect(() => {
        setConstruction((prev) => ({
            ...prev,
            settings: {
                ...defaultSettings,
                ...prev.settings,
            },
        }));
    }, [defaultSettings]);

    const hasConstructions = !!order.constructions?.length;
    const hasComment = !!order.comment?.trim().length;

    const isNextBtnDisabled = loading || !hasConstructions || (isCommentRequired && !hasComment);

    const saveDraftConstructions = async () => {
        await saveOrderDraft({
            variables: { orderId: order._id, order: actionOrderConstructionsDraft(order) },
        });
    };

    const isAnyTeethSelected = () => {
        return (construction.teethNumbers?.length ?? 0) > 0 || !!construction.teethRange;
    };

    const isAllPropertiesValid = (settings: ActionConstructionPropertyValues) => {
        return properties.every((prop) => {
            if (!prop.isRequired) return true;

            const key = toCamelCase(prop.type.toString());
            const value = settings?.[key as keyof ActionConstructionPropertyValues];

            if (key === 'material') {
                return !!settings?.material || !!settings?.otherMaterial;
            }

            if (key === 'shade') {
                if (settings?.shade) {
                    return !!settings?.color || !!settings?.colorSampling;
                }
                // A color without a shade means the color was entered as free text
                return !!settings?.color;
            }

            if (Array.isArray(value)) return value.length > 0;

            return value !== null && value !== undefined && value !== '';
        });
    };

    const isNumberOfUnitsValid = (numOfUnits: { header: string; numberOfUnits: number; actionId: string }[]) => {
        if (!numberOfUnitsOption.length) return true;
        if (!numOfUnits?.length) return false;
        return numOfUnits.every((nu) => nu.numberOfUnits > 0);
    };

    const isTeethValid = aggregatedTeethOption ? isAnyTeethSelected() : true;

    const isConstructionValid = useMemo(() => {
        return (
            isAllPropertiesValid(construction.settings) &&
            isNumberOfUnitsValid(construction.settings.numberOfUnits) &&
            isTeethValid
        );
    }, [
        properties,
        numberOfUnitsOption,
        construction.settings,
        aggregatedTeethOption,
        construction.teethNumbers,
        construction.teethRange,
    ]);

    const handleSettingsChanged = (settings: ActionConstructionPropertyValues) => {
        setConstruction((prev) => ({ ...prev, settings }));
    };

    const isRequired = (type: ConstructionPropertyType) => {
        return properties.some((prop) => prop.type === type && prop.isRequired);
    };

    const onSettingsSelection = (propertyName: string, settingValue: string | boolean) => {
        const updatedSettings = { ...construction.settings, [propertyName]: settingValue };
        setConstruction((prev) => ({ ...prev, settings: updatedSettings }));
    };

    const onShadeChange = (properties: { shade: string; color: string; colorSampling: boolean }) => {
        const updatedSettings = { ...construction.settings, ...properties };
        setConstruction((prev) => ({ ...prev, settings: updatedSettings }));
    };

    const onMaterialsChange = (properties: { material: string; otherMaterial: string; materialId?: string }) => {
        if (properties.material) {
            const selectedMaterial = availableMaterialObjs?.find((mat) => mat.material === properties.material);
            if (selectedMaterial) {
                properties.material = selectedMaterial.material;
                properties.materialId = selectedMaterial._id;
            }
        }
        const updatedSettings = { ...construction.settings, ...properties };
        setConstruction((prev) => ({ ...prev, settings: updatedSettings }));
    };

    const onNumberOfUnitsChange = (value: number, header: string, actionId: string) => {
        const numberOfUnitsValues = construction.settings?.numberOfUnits || [];
        const existing = [...numberOfUnitsValues].find((nu) => nu.header === header);
        let updatedValues = [];
        if (existing) {
            updatedValues = numberOfUnitsValues.map((nu) =>
                nu.header === header ? { ...nu, numberOfUnits: value, actionId } : nu,
            );
        } else {
            updatedValues = [...numberOfUnitsValues, { header, numberOfUnits: value, actionId }];
        }
        const updatedSettings = { ...construction.settings, numberOfUnits: updatedValues };
        setConstruction((prev) => ({ ...prev, settings: updatedSettings }));
    };

    const onTeethSettingsChange = (propertyName: string, settingValue: { id: number }[]) => {
        const updatedSettings = { ...construction.settings, [propertyName]: settingValue };
        setConstruction((prev) => ({ ...prev, settings: updatedSettings }));
    };

    const handleTeethSelectionChanged = (selectedTeeth?: number[] | undefined, teethRange?: TeethRange | undefined) => {
        setConstruction((prev) => ({ ...prev, teethNumbers: selectedTeeth, teethRange }));
    };

    const handleActionsChanged = (actions: Action[]) => {
        setConstruction((prev) => ({ ...prev, actions }));
    };

    const value = useMemo<ConstructionContextType>(
        () => ({
            saveDraftConstructions,
            isLoading: loading,
            isNextBtnDisabled,
            isCommentRequired,
            isThreeShapeEnabled,
            primaryAction,
            subCategoryId,
            numberOfUnitsOption,
            properties,
            isConstructionValid,
            handleSettingsChanged,
            onSettingsSelection,
            onShadeChange,
            availableMaterials,
            availableMaterialObjs,
            aggregatedTeethOption,
            onMaterialsChange,
            onNumberOfUnitsChange,
            numberOfUnitsValues: construction.settings?.numberOfUnits || [],
            onTeethSettingsChange,
            hasType,
            isRequired,
            construction,
            handleTeethSelectionChanged,
            handleActionsChanged,
            setConstruction,
        }),
        [
            loading,
            order,
            isNextBtnDisabled,
            isCommentRequired,
            isThreeShapeEnabled,
            primaryAction,
            subCategoryId,
            numberOfUnitsOption,
            properties,
            isConstructionValid,
            availableMaterials,
            availableMaterialObjs,
            aggregatedTeethOption,
            construction,
        ],
    );

    return <ConstructionContext.Provider value={value}>{children}</ConstructionContext.Provider>;
}

export function useConstructions() {
    const context = useContext(ConstructionContext);
    if (!context) {
        throw new Error('useConstructions must be used within a ConstructionProvider');
    }
    return context;
}
